import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';

// Fallback a los diálogos nativos cuando no hay provider (p. ej. en tests aislados)
const defaultValue = {
  notify: (message) => window.alert(message),
  confirm: ({ message }) => Promise.resolve(window.confirm(message)),
};

const FeedbackContext = createContext(defaultValue);

/**
 * Avisos globales (Snackbar) y diálogo de confirmación con el estilo de la app.
 *
 *   const notify = useNotify();
 *   notify('Cliente actualizado', 'success');
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: '¿Eliminar esta ruta?', danger: true }))) return;
 */
export const FeedbackProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const notify = useCallback((message, severity = 'info') => {
    setToast({ message, severity, key: Date.now() });
  }, []);

  const confirm = useCallback((options) => {
    setDialog({
      title: 'Confirmar',
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      danger: false,
      ...options,
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const closeDialog = (result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialog(null);
  };

  const handleToastClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setToast(null);
  };

  return (
    <FeedbackContext.Provider value={{ notify, confirm }}>
      {children}

      <Snackbar
        key={toast?.key}
        open={Boolean(toast)}
        autoHideDuration={toast?.severity === 'error' ? 8000 : 5000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert onClose={handleToastClose} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <Dialog open={Boolean(dialog)} onClose={() => closeDialog(false)} maxWidth="xs" fullWidth>
        {dialog && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>{dialog.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{dialog.message}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => closeDialog(false)} color="inherit">
                {dialog.cancelText}
              </Button>
              <Button
                onClick={() => closeDialog(true)}
                variant="contained"
                color={dialog.danger ? 'error' : 'primary'}
                autoFocus
              >
                {dialog.confirmText}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </FeedbackContext.Provider>
  );
};

export const useNotify = () => useContext(FeedbackContext).notify;
export const useConfirm = () => useContext(FeedbackContext).confirm;
